import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { listOrders, DraftOrder, OrderStats } from '../../services/adminApi';

type FilterStatus = 'all' | 'open' | 'invoice_sent' | 'completed';
type OrderSortField = 'order' | 'customer' | 'total' | 'status' | 'date';
type SortDirection = 'asc' | 'desc';

/**
 * Admin Orders Page
 *
 * Displays Shopify draft orders from the wholesale portal
 */
export default function AdminOrders() {
  const [orders, setOrders] = useState<DraftOrder[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [sortField, setSortField] = useState<OrderSortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const toggleSort = (field: OrderSortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listOrders();
      setOrders(response.orders);
      setStats(response.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    if (filter === 'all') return true;
    const statusMap: Record<string, string> = {
      open: 'OPEN',
      invoice_sent: 'INVOICE_SENT',
      completed: 'COMPLETED',
    };
    return order.status === statusMap[filter];
  }).sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'order':
        return dir * a.name.localeCompare(b.name, undefined, { numeric: true });
      case 'customer':
        return dir * (a.customer?.name || '').localeCompare(b.customer?.name || '');
      case 'total':
        return dir * (parseFloat(a.totalPrice) - parseFloat(b.totalPrice));
      case 'status':
        return dir * a.status.localeCompare(b.status);
      case 'date':
        return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button onClick={fetchData} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Orders" value={stats?.total || 0} />
        <StatCard label="Open" value={stats?.open || 0} color="amber" />
        <StatCard label="Invoice Sent" value={stats?.invoiceSent || 0} color="blue" />
        <StatCard label="Completed" value={stats?.completed || 0} color="green" />
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'open', 'invoice_sent', 'completed'] as FilterStatus[]).map(
          (status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wide rounded transition-colors ${
                filter === status
                  ? 'bg-leather text-cream'
                  : 'bg-white/5 text-cream hover:bg-white/10'
              }`}
            >
              {status.replace('_', ' ')}
            </button>
          )
        )}
      </div>

      {/* Orders Table */}
      <div className="card overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <svg
              className="w-12 h-12 mx-auto mb-4 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p>No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] text-left bg-white/5">
                  <OrderSortableHeader field="order" label="Order" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <OrderSortableHeader field="customer" label="Customer" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    Items
                  </th>
                  <OrderSortableHeader field="total" label="Total" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <OrderSortableHeader field="status" label="Status" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <OrderSortableHeader field="date" label="Date" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    expanded={expandedOrder === order.id}
                    onToggle={() =>
                      setExpandedOrder(
                        expandedOrder === order.id ? null : order.id
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-text-muted text-center">
        Showing {filteredOrders.length} of {orders.length} orders
      </div>
    </div>
  );
}

/**
 * Sortable Table Header
 */
function OrderSortableHeader({
  field,
  label,
  current,
  direction,
  onSort,
}: {
  field: OrderSortField;
  label: string;
  current: OrderSortField;
  direction: SortDirection;
  onSort: (field: OrderSortField) => void;
}) {
  const isActive = current === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium cursor-pointer hover:text-cream select-none"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={direction === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        ) : (
          <svg className="w-3 h-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        )}
      </span>
    </th>
  );
}

/**
 * Stat Card Component
 */
function StatCard({
  label,
  value,
  color = 'leather',
}: {
  label: string;
  value: number;
  color?: 'leather' | 'amber' | 'blue' | 'green';
}) {
  const colorClasses = {
    leather: 'bg-white/5 text-cream',
    amber: 'bg-amber-900/30 text-amber-300',
    blue: 'bg-blue-900/30 text-blue-300',
    green: 'bg-green-900/30 text-green-300',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg ${colorClasses[color]}`}
    >
      <div className="text-2xl font-display">{value}</div>
      <div className="text-sm opacity-80 uppercase tracking-wide">{label}</div>
    </motion.div>
  );
}

/**
 * Order Row Component
 */
function OrderRow({
  order,
  expanded,
  onToggle,
}: {
  order: DraftOrder;
  expanded: boolean;
  onToggle: () => void;
}) {
  const totalItems = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border-b border-white/[0.04] hover:bg-white/5 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-4">
          <div className="font-medium text-cream">{order.name}</div>
        </td>
        <td className="px-4 py-4">
          {order.customer ? (
            <div>
              <div className="text-sm font-medium">{order.customer.name}</div>
              <div className="text-xs text-text-muted">{order.customer.email}</div>
            </div>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </td>
        <td className="px-4 py-4 text-sm">
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </td>
        <td className="px-4 py-4 font-medium">
          ${parseFloat(order.totalPrice).toFixed(2)}
        </td>
        <td className="px-4 py-4">
          <OrderStatusBadge status={order.status} />
        </td>
        <td className="px-4 py-4 text-sm text-text-muted">
          {formatDate(order.createdAt)}
        </td>
        <td className="px-4 py-4 text-right">
          {order.invoiceUrl && (
            <a
              href={order.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-brass hover:underline"
            >
              View Invoice
            </a>
          )}
        </td>
      </motion.tr>

      {/* Expanded row with line items */}
      {expanded && (
        <tr className="bg-white/5">
          <td colSpan={7} className="px-4 py-4">
            <div className="pl-4 border-l-2 border-brass">
              <h4 className="text-sm font-medium text-cream mb-3">
                Line Items
              </h4>
              <div className="space-y-2">
                {order.lineItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm"
                  >
                    <div>
                      <span className="font-medium">{item.title}</span>
                      {item.sku && (
                        <span className="text-text-muted ml-2">
                          ({item.sku})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-text-muted">x{item.quantity}</span>
                      <span className="line-through text-text-muted">
                        ${item.originalPrice}
                      </span>
                      <span className="font-medium text-green-400">
                        ${item.discountedPrice}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {order.note && (
                <div className="mt-4 pt-4 border-t border-white/[0.06]">
                  <h4 className="text-sm font-medium text-cream mb-1">
                    Note
                  </h4>
                  <p className="text-sm text-text-muted">{order.note}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Order Status Badge
 */
function OrderStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    OPEN: 'bg-amber-900/40 text-amber-300',
    INVOICE_SENT: 'bg-blue-900/40 text-blue-300',
    COMPLETED: 'bg-green-900/40 text-green-300',
  };

  const labels: Record<string, string> = {
    OPEN: 'Open',
    INVOICE_SENT: 'Invoice Sent',
    COMPLETED: 'Completed',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs rounded ${
        colors[status] || 'bg-white/5 text-text-muted'
      }`}
    >
      {labels[status] || status}
    </span>
  );
}

/**
 * Format date string
 */
function formatDate(dateString: string): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
